import SettingsMenu from './settings_menu';
import { BitrateManager } from '@javascripts/bitrate_manager';
import ConnectionAdapter from '@javascripts/adapters/connection_adapter';
import { PlayerAhoyCommonEvent } from '@javascripts/player_ahoy_common_event';
import SubtitleManager from '../subtitle_manager';
import TopicAdapter from '@javascripts/adapters/topic_adapter';
import FullscreenApiAdapter from '@javascripts/adapters/fullscreen_api_adapter';
import isFeatureToggleOn from "@javascripts/utils/feature_toggle";

export default class SettingsMenuPanel {
  constructor(player, adapter, options = {}){
    this._el = document.createElement('div');
    this._player = player;
    this._adapter = adapter;
    this._options = options;
    this.menu = null;

    this.player.on("loadedmetadata", () => {
      this.subtitleManager = new SubtitleManager(this.player, this.options);
      this.subtitleManager.setPreferredSub();
      this.buildMenu()
    });
  }

  buildMenu() {
    this.settingMenu = this.initSettingMenu(this.subtitleManager);
    this.videoQualityMenu = this.initVideoQualityMenu(
      this.adapter,
      this.options.resolutionMapping,
      this.sendBitrateEvent.bind(this)
    );
    this.subtitleMenu = this.initSubtitleMenu(
      this.player,
      this.subtitleManager
    );

    if(isFeatureToggleOn('enable_playback_speed')) {
      this.playbackRatesMenu = this.initPlaybackSpeedMenu(
        this.player
      )
    }

    this.menu = this.settingMenu;

    this.build();
    this.hide();
  }

  get options() {
    return this._options;
  }

  get adapter() {
    return this._adapter;
  }

  get player() {
    return this._player;
  }

  el() {
    return this._el;
  }

  initPlaybackSpeedMenu(player) {
    const playbackRates = [
      {
        label: "0.5x",
        menuType: player.playbackRate() === 0.5 ? 'checked' : undefined,
        onClick: () => {
          player.playbackRate(0.5)
        }
      },
      {
        label: "0.75x",
        menuType: player.playbackRate() === 0.75 ? 'checked' : undefined,
        onClick: () => {
          player.playbackRate(0.75)
        }
      },
      {
        label: "Normal",
        menuType: player.playbackRate() === 1 ? 'checked' : undefined,
        onClick: () => {
          player.playbackRate(1)
        }
      },
      {
        label: "1.25x",
        menuType: player.playbackRate() === 1.25 ? 'checked' : undefined,
        onClick: () => {
          player.playbackRate(1.25)
        }
      },
      {
        label: "1.5x",
        menuType: player.playbackRate() === 1.5 ? 'checked' : undefined,
        onClick: () => {
          player.playbackRate(1.5)
        }
      }
    ]

    const playbackSpeedMenu = new SettingsMenu({
      title: "Playback Speed",
      showBackButton: true,
      onClickBack: this.handleBack.bind(this),
      menuItems: playbackRates
    });
    playbackSpeedMenu.updateList(playbackRates);
    return playbackSpeedMenu;
  }

  initSubtitleMenu(player,subtitleManager) {
    let subtitles = [];
    const subtitleMenu = new SettingsMenu({
      title: "Subtitle",
      showBackButton: true,
      onClickBack: this.handleBack.bind(this),
      menuItems: subtitles
    });

    const triggerSubtitleChangeEvent = (selectedTrack) => {
      player.trigger({type: "vp-subtitle-change", id: this.options.videoId, track: selectedTrack});
    }
    const sendSubtitleEvent = (action, label) => {
      if (!subtitleManager) return;

      const params = {
        action: action,
        subtitle: label,
        video_id: this.options.videoId,
        play_uuid: this.options.play_uuid
      }

      PlayerAhoyCommonEvent.sendSubtitle(params);
    }
    subtitles = subtitleManager.getSubtitleMenu().map((subtitle) => {
      return {
        label: subtitle.label,
        menuType: subtitle.active ? 'checked' : undefined,
        onClick: () => {
          subtitle.onClick();
          triggerSubtitleChangeEvent(subtitle.label);
          sendSubtitleEvent('select', subtitle.label);
        }
      }
    });
    subtitleMenu.updateList(subtitles);
    return subtitleMenu;
  }

  initVideoQualityMenu(adapter, resolutionMapping, onSelectQualityCallback) {
    let videoQualities = []
    const videoQualityMenu = new SettingsMenu({
      title: "Video Quality",
      showBackButton: true,
      onClickBack: this.handleBack.bind(this),
      menuItems: videoQualities
    });

    const bitrateManager = new BitrateManager(adapter, resolutionMapping)
    videoQualities = bitrateManager.getResolutions()
      .map((quality) => {
        return {
          menuType: quality.label === 'Auto'? 'checked' : undefined,
          label: quality.label,
          onClick: () => {
            quality.enableQuality()
            setTimeout(() => {
              onSelectQualityCallback('select')
            }, 100)
          }
        }
      })

    videoQualityMenu.updateList(videoQualities);
    return videoQualityMenu;
  }

  initSettingMenu(subtitleManager) {
    const triggerSubtitleClickEvent = () => {
      this.player.trigger({type: "vp-subtitle-click", id: this.options.videoId});
    }

    const videoQualityMenuItem = {
      label: "Video Quality",
      menuType: "arrow",
      enabled: true,
      onClick: () => {
        this.changeMenu("Video Quality");
        this.sendBitrateEvent('open');
        PlayerAhoyCommonEvent.sendPlaybackBitrateShow(this.options.play_uuid);
      }
    };

    const subtitleMenuItem = {
      label: "Subtitle",
      menuType: "arrow",
      enabled: subtitleManager.textTracks().length > 0,
      onClick: () => {
        this.changeMenu("Subtitle");
        triggerSubtitleClickEvent();
      }
    };

    const playbackSpeedMenuItem = {
      label: "Playback Speed",
      menuType: "arrow",
      enabled: this.options.type === "Video",
      onClick: () => {
        this.changeMenu("Playback Speed");
      }
    };

    const sendFeedbackMenuItem = {
      label: "Send Feedback",
      menuType: "arrow",
      enabled: true,
      onClick: () => {
        FullscreenApiAdapter.exitFullscreen();
        TopicAdapter.publish('watchpage/send_feedback/show', {
          issue_category_code: 'icc4'
        })
      }
    }

    const menuItems = [videoQualityMenuItem, subtitleMenuItem, sendFeedbackMenuItem];
    if(isFeatureToggleOn('enable_playback_speed')) {
      menuItems.splice(2, 0, playbackSpeedMenuItem);
    }

    const settingMenu = new SettingsMenu({
      title: "Settings",
      showBackButton: false,
      menuItems: menuItems
    });
    return settingMenu;
  }

  sendBitrateEvent(action) {
    const attr = this.adapter.bitratesObject().getCurrentMediaAttribute();
    const params = {
      play_uuid: this.options.play_uuid,
      action: action,
      quality: window['playback_selected_quality']?.[this.adapter.id()] || null,
      bandwidth: attr.bitrate,
      network_type: ConnectionAdapter.effectiveType(),
      content_type: this.options.type
    }
    PlayerAhoyCommonEvent.sendBitrate(params);
  }

  build() {
    this.el().className = 'vjs-settings-button__panel';
    this.el().textContent = ''
    this.el().appendChild(this.menu.el());
  }

  show() {
    this.el().style.display = "block";
    this.menu.show();
    this.checkPlayerInactive();
  }

  hide() {
    this.el().style.display = "none";
    this.changeMenu("Settings")
  }

  displayStatus() {
    return getComputedStyle(this.el()).display;
  }

  toggle(){
    if (this.displayStatus() == "none") {
      this.show();
    }else{
      this.hide();
    }
  }

  handleBack() {
    this.changeMenu("Settings");
  }

  replaceMenu(newMenu) {
    if(newMenu !== undefined && this.menu !== undefined) {
      this.el().replaceChild(newMenu.el(), this.menu.el());
      this.menu = newMenu;
    }
  }

  changeMenu(menuName) {
    if (menuName === "Subtitle") {
      this.replaceMenu(this.subtitleMenu);
    } else if(menuName === "Video Quality") {
      this.replaceMenu(this.videoQualityMenu);
    } else if(menuName === "Playback Speed") {
      this.replaceMenu(this.playbackRatesMenu);
    } else {
      this.replaceMenu(this.settingMenu);
    }
    this.menu.show();
  }

  checkPlayerInactive() {
    const element = document.querySelector('.video-js');

    const interval = setInterval(() => {
      if (element.classList.contains('vjs-user-inactive') && !element.classList.contains('vjs-paused')) {
        clearInterval(interval);
        this.hide();
      }
    }, 100);
  }
}
